import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useSession } from '../contexts/SessionContext';
import { get } from '../utils/api';
import { searchCachedProducts } from '../utils/cacheWarm';
import { debounce } from 'lodash';
import PageLayout from './common/PageLayout';
import { useKeyboard } from '../context/KeyboardContext';
import ProductVariationDialog from './ProductVariationDialog';
import { getLineChargeTotal } from '../utils/cartPricing';
import kotilingaSlides, { DEFAULT_SUBTITLE } from '../config/kotilingaSlides';

// Material-UI Components
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  Remove as RemoveIcon,
  Cached as CachedIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShoppingCart as ShoppingCartIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Backspace as BackspaceIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import _ from 'lodash';

// Helper: parse allowed categories from env (comma-separated names)
const getAllowedCategories = () => {
  const env = process.env.REACT_APP_KOTILINGA_PRODUCT_CATEGORIES || '';
  return env
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
};

const KotilingaPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [expandedProducts, setExpandedProducts] = useState({});
  const [quantities, setQuantities] = useState({});
  // Quick Add state
  const [quickAddRows, setQuickAddRows] = useState([]); // array of arrays of product IDs
  const [quickAddProductsMap, setQuickAddProductsMap] = useState({}); // id -> product
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [dialogProduct, setDialogProduct] = useState(null);
  const { addToCart, removeFromCart, cart } = useCart();
  const { session, setLastOrigin } = useSession();
  const navigate = useNavigate();
  const [showCheckoutButton, setShowCheckoutButton] = useState(false);
  const kb = useKeyboard();
  const detailsIframeRef = useRef(null);
  // Details dialog state
  const [detailsDialog, setDetailsDialog] = useState({ open: false, url: '', title: '', loading: false });

  // Slider state
  const [slideIndex, setSlideIndex] = useState(0);
  const autoPlayRef = useRef(null);
  const slideCount = Array.isArray(kotilingaSlides) ? kotilingaSlides.length : 0;
  // Preload state
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  // Helpers to manage a single autoplay interval
  const clearAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    clearAutoPlay();
    if (!slideCount) return;
    autoPlayRef.current = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slideCount);
    }, 5000);
  }, [clearAutoPlay, slideCount]);

  const goToNext = useCallback(() => {
    if (!slideCount) return;
    setSlideIndex((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  const goToPrev = useCallback(() => {
    if (!slideCount) return;
    setSlideIndex((prev) => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  // Auto-play every 5 seconds (single interval) and clean up on unmount
  useEffect(() => {
    if (!slideCount) return;
    startAutoPlay();
    return () => {
      clearAutoPlay();
    };
  }, [startAutoPlay, clearAutoPlay, slideCount]);

  // Preload all slider images to cache them and reduce first-paint delay
  useEffect(() => {
    try {
      const sources = Array.from(
        new Set(
          (Array.isArray(kotilingaSlides) ? kotilingaSlides : [])
            .map((s) => s && s.image_src)
            .filter(Boolean)
        )
      );
      if (sources.length === 0) {
        setImagesPreloaded(true);
        return;
      }

      let loaded = 0;
      const imgs = sources.map((src) => {
        const img = new Image();
        // Hints for modern browsers
        try { img.decoding = 'async'; } catch {}
        try { img.loading = 'eager'; } catch {}
        img.onload = img.onerror = () => {
          loaded += 1;
          if (loaded >= sources.length) {
            setImagesPreloaded(true);
          }
        };
        img.src = src;
        return img;
      });

      return () => {
        imgs.forEach((i) => { i.onload = null; i.onerror = null; });
      };
    } catch (e) {
      setImagesPreloaded(true);
    }
  }, []);

  // Image dialog state
  const [imageDialog, setImageDialog] = useState({ open: false, src: '', title: '' });

  // Open details dialog for a product if permalink exists
  const openDetails = (product) => {
    const url = product?.permalink || product?.url || '';
    if (!url) return;
    setDetailsDialog({ open: true, url, title: product?.name || 'Details', loading: true });
  };
  const closeDetails = () => setDetailsDialog(prev => ({ ...prev, open: false }));

  // Show snackbar notification
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  // Handle snackbar close
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Quick add helpers
  const openVariationDialog = (product) => {
    setDialogProduct(product);
    setVariationDialogOpen(true);
  };

  const closeVariationDialog = () => {
    setVariationDialogOpen(false);
    setDialogProduct(null);
  };

  const handleQuickAddSimple = (product) => {
    const cartItemId = `${product.id}-0`;
    const imageUrl = product.image || product.images?.[0]?.src || '';
    addToCart({
      id: cartItemId,
      cart_item_id: cartItemId,
      name: product.name,
      price: parseFloat(product.price) || 0,
      quantity: 1,
      product_id: product.id,
      variation_id: 0,
      image: imageUrl,
      sku: product.sku || ''
    });
    setShowCheckoutButton(true);
  };

  const onDialogAddToCart = (item) => {
    addToCart(item);
    setShowCheckoutButton(true);
  };

  // Category filtering for Kotilinga
  const allowed = getAllowedCategories();
  const filterByCategory = useCallback((list) => {
    if (!allowed.length) return list; // if not configured, show all
    return list.filter(p => (p.categories || []).some(c => allowed.includes((c?.name || '').toLowerCase())));
  }, [allowed]);

  // Fetch products from API
  const fetchProducts = useCallback(async (search = '') => {
    try {
      if (search && search.trim().length < 3) {
        // Do not search for fewer than 3 characters
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const endpoint = search ? `/api/products?search=${encodeURIComponent(search)}` : '/api/products';
      const response = await get(endpoint);
      const list = response && Array.isArray(response.data) ? response.data : [];
      // Apply category filter immediately to ensure only allowed categories are used downstream
      const filtered = filterByCategory(list);
      setProducts(filtered);
      setError('');
    } catch (err) {
      console.error('Error fetching products:', err);
      // Live fetch failed (e.g. network blip) → fall back to the cached catalog snapshot,
      // then apply the same category filter so only Kotilinga products are shown.
      const cached = searchCachedProducts(search);
      const filteredCache = Array.isArray(cached) ? filterByCategory(cached) : [];
      if (filteredCache.length > 0) {
        console.warn(`Products live fetch failed; showing ${filteredCache.length} from cached catalog`);
        setProducts(filteredCache);
        setError('');
      } else {
        setProducts([]);
        setError('Please wait while we are loading the information');
        showSnackbar('Loading product information, please wait...', 'info');
      }
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, filterByCategory]);

  // Do not auto-fetch on mount; wait for user to type at least 3 chars
  useEffect(() => {
    setLoading(false);
    try { setLastOrigin && setLastOrigin('kotilinga'); } catch {}
    return () => {};
  }, []);

  // Parse quick add env and fetch products by IDs for Quick Add section
  useEffect(() => {
    const env = process.env.REACT_APP_KOTILINGA_PRODUCTS || '';
    if (!env.trim()) return;

    const rows = env.split('|')
      .map(row => row.split(',')
        .map(s => parseInt(String(s).trim(), 10))
        .filter(n => !isNaN(n))
      )
      .filter(arr => arr.length > 0);

    if (rows.length === 0) return;
    setQuickAddRows(rows);

    const allIds = Array.from(new Set(rows.flat()));
    const query = allIds.join(',');

    const loadQuickAdd = async () => {
      try {
        const resp = await get(`/api/products?productId=${encodeURIComponent(query)}`);
        const list = Array.isArray(resp?.products)
          ? resp.products
          : (Array.isArray(resp?.data) ? resp.data : []);
        const map = {};
        list.forEach(p => { map[p.id] = p; });
        setQuickAddProductsMap(map);
      } catch (e) {
        console.error('Failed to load quick add products:', e);
      }
    };

    loadQuickAdd();
  }, []);

  // Debounced search
  const debouncedSearchRef = useRef(
    _.debounce((term) => {
      if (!term || term.trim().length < 3) return;
      fetchProducts(term);
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    const trimmed = term.trim();
    if (trimmed.length >= 3) {
      debouncedSearchRef(term);
    } else if (trimmed.length === 0) {
      // Clear results when search is cleared
      setProducts([]);
      setLoading(false);
    } else {
      // 1-2 chars: do nothing and keep current results cleared
      setProducts([]);
      setLoading(false);
    }
  };

  const openSearchKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[KotilingaPage] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open('alpha', { showEnter: true, position: 'lowerCenter', widthPct: 0.85, heightVh: 35, autoHeight: true }, {
      onInput: (ch) => {
        setSearchTerm((prev) => {
          const next = (prev || '') + ch;
          const t = next.trim();
          if (t.length >= 3) {
            debouncedSearchRef(next);
          } else if (t.length === 0) {
            setProducts([]);
            setLoading(false);
          }
          return next;
        });
      },
      onBackspace: () => {
        setSearchTerm((prev) => {
          const next = (prev || '') .slice(0, -1);
          const t = next.trim();
          if (t.length >= 3) {
            debouncedSearchRef(next);
          } else if (t.length === 0) {
            setProducts([]);
            setLoading(false);
          } else {
            setProducts([]);
            setLoading(false);
          }
          return next;
        });
      },
      onEnter: () => {
        kb.close();
      },
      onClear: () => {
        setSearchTerm('');
        setProducts([]);
        setLoading(false);
      }
    });
  }, [kb, debouncedSearchRef, fetchProducts, searchTerm]);

  // Quantity change for variations
  const handleQuantityChange = (productId, variationId, value) => {
    const quantity = Math.max(1, Math.min(100, parseInt(value) || 1));
    const key = variationId ? `${productId}-${variationId}` : `${productId}-0`;
    setQuantities(prev => ({ ...prev, [key]: quantity }));
  };

  const isInCart = (product, variation = null) => {
    const variationId = variation ? variation.id : 0;
    return cart.some(item => 
      item.product_id === product.id && 
      (variationId === 0 ? !item.variation_id || item.variation_id === 0 : item.variation_id === variationId)
    );
  };

  const toggleCartItem = (product, variation = null) => {
    const cartItemId = variation ? `${product.id}-${variation.id}` : `${product.id}-0`;
    const quantityKey = variation ? `${product.id}-${variation.id}` : `${product.id}-0`;
    const quantity = quantities[quantityKey] || 1;

    let displayName = product.name;
    const productImage = product.image;
    if (variation) {
      const variationName = variation.attributes
        ? Object.values(variation.attributes).map(attr => attr.option).join(', ')
        : variation.name || 'Selected Option';
      displayName = `${product.name} - ${variationName}`;
    }

    if (isInCart(product, variation)) {
      removeFromCart(cartItemId);
    } else {
      addToCart({
        id: cartItemId,
        cart_item_id: cartItemId,
        name: displayName,
        price: parseFloat(variation ? variation.price : product.price),
        quantity,
        product_id: product.id,
        variation_id: variation ? variation.id : 0,
        image: productImage,
        sku: variation ? variation.sku : product.sku || ''
      });
      setShowCheckoutButton(true);
    }
  };

  // Formatting helpers
  const formatPrice = (price) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price);

  const hasVariations = (product) => product.type === 'variable' && product.variations && product.variations.length > 0;

  const getProductCategory = (product) => product.categories?.[0]?.name || 'Uncategorized';

  const formatVariationName = (name) => {
    if (!name) return '';
    const withSpaces = name.replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    return withSpaces.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  

  // Products are already category-filtered in fetchProducts
  const filteredProducts = React.useMemo(() => products, [products]);

  const displayedProducts = searchTerm.trim() ? filteredProducts.filter(product => {
    const term = searchTerm.toLowerCase().trim();
    const nameMatch = product.name && product.name.toLowerCase().includes(term);
    const skuMatch = product.sku && product.sku.toLowerCase().includes(term);
    const categoryMatch = product.categories && product.categories.some(cat => 
      cat && cat.name && cat.name.toLowerCase().includes(term)
    );
    return nameMatch || skuMatch || categoryMatch;
  }) : filteredProducts;

  // Render variations
  const renderVariations = (product) => {
    if (!hasVariations(product)) return null;
    return (
      <Collapse in={!!expandedProducts[product.id]} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {product.variations.map((variation) => (
            <React.Fragment key={variation.id}>
              <Divider />
              <ListItem 
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={quantities[`${product.id}-${variation.id}`] || 1}
                      onChange={(e) => handleQuantityChange(product.id, variation.id, e.target.value)}
                      inputProps={{ min: 1, max: 100, style: { width: '60px', textAlign: 'center' } }}
                      sx={{ '& .MuiOutlinedInput-root input': { textAlign: 'center', padding: '8px 4px' } }}
                    />
                    <Button
                      variant={isInCart(product, variation) ? 'outlined' : 'contained'}
                      color={isInCart(product, variation) ? 'secondary' : 'primary'}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleCartItem(product, variation); }}
                      sx={{ minWidth: '100px' }}
                    >
                      {isInCart(product, variation) ? 'Remove' : 'Add'}
                    </Button>
                  </Box>
                } 
                disablePadding
              >
                <ListItemButton>
                  <ListItemText 
                    primary={formatVariationName(variation.name)} 
                    secondary={formatPrice(variation.price)}
                    sx={{ ml: 2 }}
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Collapse>
    );
  };

  // Render a product item
  const renderProductItem = (product) => {
    const isExpanded = !!expandedProducts[product.id];
    const variations = hasVariations(product);
    const thumbUrl = product.image || product.images?.[0]?.src || '';

    return (
      <React.Fragment key={product.id}>
        <ListItem 
          alignItems="flex-start"
          sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 1 } }}
        >
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
            {/* Thumbnail */}
            <Box
              sx={{
                flex: '0 0 auto',
                width: 96,
                height: 96,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                cursor: thumbUrl ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (!thumbUrl) return;
                const fullImageUrl = product?.full_image || (product?.images && product.images[0]?.src) || thumbUrl;
                setImageDialog({ open: true, src: fullImageUrl, title: product.name || 'Image' });
              }}
            >
              {thumbUrl ? (
                <img src={thumbUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>No image</Typography>
              )}
            </Box>
            {/* Details */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.15 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography 
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 0, lineHeight: 1.2 }}
                    >
                      {product.name}
                    </Typography>
                    {product?.permalink && (
                      <Button 
                        size="small" 
                        variant="contained" 
                        color="secondary"
                        onClick={() => openDetails(product)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 1.5,
                          boxShadow: 1,
                          px: 1.25,
                          py: 0.2,
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          '&:hover': { boxShadow: 2 }
                        }}
                      >
                        Details
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {hasVariations(product) ? getPriceRange(product) : formatPrice(product.price)}
                    </Typography>
                    {!variations ? (
                      <Button
                        variant={isInCart(product) ? 'outlined' : 'contained'}
                        color={isInCart(product) ? 'secondary' : 'primary'}
                        size="small"
                        onClick={() => toggleCartItem(product)}
                        sx={{ mt: 0.5, width: '100%' }}
                      >
                        {isInCart(product) ? 'Remove from Cart' : 'Add to Cart'}
                      </Button>
                    ) : (
                      <Button
                        variant={isInCart(product) ? 'outlined' : 'outlined'}
                        color={isInCart(product) ? 'secondary' : 'primary'}
                        size="small"
                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => setExpandedProducts(prev => ({ ...prev, [product.id]: !prev[product.id] }))}
                        sx={{ mt: 0.5, width: '100%' }}
                      >
                        {isExpanded ? 'Hide Options' : 'Show Options'}
                      </Button>
                    )}
                  </Box>
                </Box>
                <Chip 
                  label={getProductCategory(product)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Box>
        </ListItem>
        {variations && (
          <Box sx={{ pl: 4, pr: 2 }}>
            {renderVariations(product)}
          </Box>
        )}
      </React.Fragment>
    );
  };

  const getPriceRange = (product) => {
    if (!hasVariations(product) || !product.variations || product.variations.length === 0) {
      return formatPrice(product.price);
    }
    const prices = product.variations.map(v => parseFloat(v.price)).filter(p => !isNaN(p));
    if (prices.length === 0) return 'Price not available';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? formatPrice(min) : `${formatPrice(min)} - ${formatPrice(max)}`;
  };

  // Render products list based on search and filters
  const renderProducts = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => { setRefreshing(true); await fetchProducts(searchTerm); setRefreshing(false); }}
            startIcon={<CachedIcon />}
            disabled={refreshing}
            sx={{ mt: 2 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Page'}
          </Button>
        </Container>
      );
    }

    if (searchTerm.trim().length >= 3 && displayedProducts.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography>No products found matching your search</Typography>
        </Box>
      );
    }

    if (!searchTerm || searchTerm.trim().length < 3) {
      return null;
    }

    return (
      <List disablePadding>
        {displayedProducts.map(product => renderProductItem(product))}
      </List>
    );
  };

  // Cart button state
  const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
  useEffect(() => { setShowCheckoutButton(totalItems > 0); }, [totalItems]);

  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ position: 'relative', pb: showCheckoutButton ? 12 : 0 }}>
        {/* Top action buttons: Back, Home, Cart */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/home')} 
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#e1bee7',
                borderColor: '#b39ddb',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#d1c4e9',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              startIcon={<HomeIcon />} 
              onClick={() => navigate('/home')} 
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#e1bee7',
                borderColor: '#b39ddb',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#d1c4e9',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Home
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/cart', { state: { from: 'kotilinga' } })} 
              startIcon={
                <Badge badgeContent={totalItems} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              } 
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#e1bee7',
                borderColor: '#b39ddb',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#d1c4e9',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Cart
            </Button>
          </Box>
        </Box>

        {/* Kotilinga Slider */}
        {slideCount > 0 && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: 220, sm: 280, md: 340 },
              mb: 3,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 2,
              // Match Quick Select box styling so letterbox sides blend in
              backgroundColor: '#f3e5f5',
              border: '1px solid #e1bee7'
            }}
            onMouseEnter={() => { clearAutoPlay(); }}
            onMouseLeave={() => { startAutoPlay(); }}
          >
            {/* Persistent top-left title overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 2,
                color: '#4a148c'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.3, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' } }}>
                Kotilinga Kshetram
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.95 }}>
                {DEFAULT_SUBTITLE}
              </Typography>
            </Box>

            {/* Bottom-right QR code panel */}
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <Typography variant="caption" sx={{ color: '#4a148c', fontWeight: 700, lineHeight: 1.2 }}>
                For more details
              </Typography>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent('http://icctmemphis.org/kotilinga')}`}
                alt="QR code to Kotilinga details"
                style={{ width: 78, height: 78, borderRadius: 6, background: 'white', padding: 4, pointerEvents: 'none', cursor: 'default' }}
              />
              <Typography variant="caption" sx={{ color: '#4a148c', opacity: 0.9 }}>
                icctmemphis.org/kotilinga
              </Typography>
            </Box>
            {/* Slides */}
            {kotilingaSlides.map((s, idx) => (
              <Box
                key={`slide-${idx}`}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: slideIndex === idx ? 1 : 0,
                  transition: 'opacity 900ms ease-in-out',
                  willChange: 'opacity',
                  pointerEvents: slideIndex === idx ? 'auto' : 'none'
                }}
                aria-hidden={slideIndex !== idx}
              >
                {/* Background image */}
                <Box
                  sx={{ position: 'absolute', inset: 0 }}
                >
                  <img
                    src={s.image_src}
                    alt={s.heading || `Slide ${idx + 1}`}
                    loading="eager"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transition: 'transform 1200ms ease-in-out',
                      transform: slideIndex === idx ? 'scale(1.0)' : 'scale(1.005)'
                    }}
                    onClick={() => {
                      const cur = kotilingaSlides[slideIndex] || s;
                      setImageDialog({ open: true, src: cur.image_src, title: cur.heading || `Slide ${slideIndex + 1}` });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        const cur = kotilingaSlides[slideIndex] || s;
                        setImageDialog({ open: true, src: cur.image_src, title: cur.heading || `Slide ${slideIndex + 1}` });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  />
                </Box>
                {/* Text overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: { xs: 2, md: 3 },
                    color: '#4a148c',
                  }}
                >
                  {s.heading && (
                    <Typography variant="h5" sx={{ fontWeight: 700, transition: 'opacity 600ms ease-in-out', opacity: slideIndex === idx ? 1 : 0 }}>
                      {s.heading}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {/* Navigation arrows */}
            <IconButton
              aria-label="Previous slide"
              onClick={() => { goToPrev(); startAutoPlay(); }}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 8,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              aria-label="Next slide"
              onClick={() => { goToNext(); startAutoPlay(); }}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 8,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }}
            >
              {/* Reuse back icon rotated for simplicity */}
              <ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>

            {/* Dots */}
            <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
              {kotilingaSlides.map((_, i) => (
                <Box
                  key={`dot-${i}`}
                  onClick={() => { setSlideIndex(i); startAutoPlay(); }}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    bgcolor: i === slideIndex ? 'white' : 'rgba(255,255,255,0.6)',
                    border: '1px solid rgba(0,0,0,0.2)'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Search Box */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>Kotilinga Kshetram - Donations Search</Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search Kotilinga donations..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={openSearchKeyboard}
              onClick={openSearchKeyboard}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="backspace"
                      edge="end"
                      size="small"
                      onClick={() => setSearchTerm((prev) => (prev || '').slice(0, -1))}
                      disabled={!searchTerm}
                      sx={{ mr: 0.5 }}
                    >
                      <BackspaceIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="clear search"
                      edge="end"
                      size="small"
                      onClick={() => { setSearchTerm(''); setProducts([]); setLoading(false); }}
                      disabled={!searchTerm}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                readOnly: !kb?.keyboardDisabled // Only readonly when virtual keyboard is enabled
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff'
                }
              }}
            />
          </Box>

          {renderProducts()}
        </Paper>

        {/* Quick Select Section */}
        {quickAddRows && quickAddRows.length > 0 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              mt: 3, 
              bgcolor: '#f3e5f5', 
              border: '1px solid #e1bee7', 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" gutterBottom>Quick Select</Typography>
            {quickAddRows.map((ids, rowIdx) => (
              <Box 
                key={`qa-row-${rowIdx}`}
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 2.5,
                  mb: 3,
                  alignItems: 'stretch',
                  justifyItems: 'center',
                  width: '100%'
                }}
              >
                {ids.map((id) => {
                  const p = quickAddProductsMap[id];
                  if (!p) return null;
                  const isVariable = p.type === 'variable';
                  const imageUrl = p.image || p.images?.[0]?.src || '';
                  return (
                    <Paper 
                      key={id} 
                      elevation={2} 
                      sx={{ 
                        p: 2, 
                        width: '100%', 
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        textAlign: 'center',
                        borderRadius: 2
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: 160, 
                          bgcolor: '#f5f5f5', 
                          borderRadius: 2, 
                          overflow: 'hidden', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          mb: 1.5,
                          cursor: imageUrl ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (!imageUrl) return;
                          const fullImageUrl = p?.full_image || (p?.images && p.images[0]?.src) || imageUrl;
                          setImageDialog({ open: true, src: fullImageUrl, title: p.name || 'Image' });
                        }}
                      >
                        {imageUrl ? (
                          <img src={imageUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>No image</Typography>
                        )}
                      </Box>
                      <Typography variant="subtitle1" noWrap title={p.name} sx={{ width: '100%', mb: 1 }}>
                        {p.name}
                      </Typography>
                      {(!isVariable && p.price) ? (
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                          {formatPrice(p.price)}
                        </Typography>
                      ) : null}
                      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={() => isVariable ? openVariationDialog(p) : handleQuickAddSimple(p)}
                          sx={{ flex: 1 }}
                        >
                          Add
                        </Button>
                        {p?.permalink && (
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => openDetails(p)}
                            sx={{ flex: 1 }}
                          >
                            Details
                          </Button>
                        )}
                      </Box>
                    </Paper>
              
    );
                })}
              </Box>
            ))}
          </Paper>
        )}

        {/* Image Preview Dialog */}
        <Dialog
          open={imageDialog.open}
          onClose={() => setImageDialog({ open: false, src: '', title: '' })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            {imageDialog.title || 'Preview'}
            <IconButton onClick={() => setImageDialog({ open: false, src: '', title: '' })} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
            {imageDialog.src && (
              <Box
                component="img"
                src={imageDialog.src}
                alt={imageDialog.title || 'Preview'}
                sx={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }}
                onError={(e) => console.error('Error loading preview:', e)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Details Dialog */}
        <Dialog
          open={detailsDialog.open}
          onClose={closeDetails}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '63vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 8 },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a1a1a1' }
            }
          }}
        >
          <DialogTitle sx={{ pr: 5 }}>
            {detailsDialog.title}
            <IconButton
              aria-label="close"
              onClick={closeDetails}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, position: 'relative', overflow: 'visible' }}>
            {detailsDialog.url ? (
              <Box sx={{ position: 'relative', minHeight: '120vh' }}>
                {detailsDialog.loading && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', zIndex: 1, bgcolor: 'rgba(255,255,255,0.85)' }}>
                    <Box sx={{ width: 42, height: 42, border: '4px solid #e2e8f0', borderTopColor: 'primary.main', borderRadius: '50%', animation: 'spin 1s linear infinite', mb: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>Please wait...</Typography>
                  </Box>
                )}
                <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
                <iframe
                  src={detailsDialog.url}
                  title={detailsDialog.title}
                  onLoad={() => setDetailsDialog(prev => ({ ...prev, loading: false }))}
                  onError={() => setDetailsDialog(prev => ({ ...prev, loading: false }))}
                  sandbox="allow-scripts"
                  ref={detailsIframeRef}
                  onFocus={() => { try { detailsIframeRef.current && detailsIframeRef.current.blur(); } catch {} }}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, pointerEvents: 'none' }}
                />
                {/* Bottom label bar to tone content and show title; non-interactive to preserve scrolling */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      px: 2,
                      py: 1.5,
                      color: 'text.primary',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 45%, rgba(255,255,255,1) 100%)',
                      textAlign: 'center',
                      fontWeight: 700
                    }}
                  >
                    {detailsDialog.title} Details
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box p={2}>
                <Typography variant="body2">Link not available.</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Image Preview Dialog */}
        <Dialog
          open={imageDialog.open}
          onClose={() => setImageDialog({ open: false, src: '', title: '' })}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ pr: 5 }}>
            {imageDialog.title}
            <IconButton
              aria-label="close"
              onClick={() => setImageDialog({ open: false, src: '', title: '' })}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {imageDialog.src ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                <img
                  src={imageDialog.src}
                  alt={imageDialog.title}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxWidth: '90vw',
                    maxHeight: 'calc(100vh - 160px)',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Variation Dialog */}
        <ProductVariationDialog open={variationDialogOpen} onClose={closeVariationDialog} product={dialogProduct} onAddToCart={onDialogAddToCart} />

        {/* Sticky Checkout Button (always visible when items exist) */}
        {showCheckoutButton && totalItems > 0 && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 12,
              zIndex: 1300,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <Box sx={{ width: '92%', maxWidth: 560, pointerEvents: 'auto', boxShadow: 3, borderRadius: '50px', bgcolor: 'white' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                onClick={() => {
                  const total = cart.reduce((sum, item) => sum + getLineChargeTotal(item), 0);
                  if (session?.userSelectedFlag) {
                    const guestUser = session?.user || {};
                    const state = {
                      cart: [...cart],
                      devotee: guestUser,
                      total,
                      fromCart: false,
                      isGuestCheckout: !session?.user?.id,
                      from: 'kotilinga'
                    };
                    navigate('/payment', { state, replace: false });
                  } else {
                    navigate('/checkout', {
                      state: {
                        cart: [...cart],
                        total,
                        from: 'kotilinga'
                      },
                      replace: false
                    });
                  }
                }}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: '50px',
                  '&:hover': {
                    transform: 'scale(1.01)',
                    boxShadow: 6
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Continue to Checkout ({totalItems} {totalItems === 1 ? 'Item' : 'Items'})
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};

export default KotilingaPage;
